import axios from 'axios';

const MILLING_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1').replace('/api/v1', '') + '/api/v1';

// Enums
export type RecipeStatus = 'draft' | 'active' | 'inactive' | 'obsolete';
export type OrderStatus = 'draft' | 'pending_approval' | 'approved' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
export type OutputType = 'main_product' | 'by_product' | 'waste';
export type CostType = 'labor' | 'overhead' | 'material' | 'depreciation' | 'utilities' | 'maintenance' | 'other';
export type AllocationMethod = 'weight' | 'volume' | 'market_value' | 'nrv' | 'physical_units' | 'none';

// Recipe Interfaces
export interface MillingRecipeInputRow {
  id?: string;
  lineNo: number;
  recipeId?: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  standardQty: number;
  isOptional?: boolean;
  warehouseId?: string;
  warehouseName?: string;
  unitCost?: number;
}

export interface MillingRecipeOutputRow {
  id?: string;
  lineNo: number;
  recipeId?: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  outputType: OutputType;
  isByProduct: boolean;
  isMainProduct: boolean;
  expectedQty: number;
  expectedYieldPercent?: number;
  costAllocationPercent?: number;
  warehouseId?: string;
  warehouseName?: string;
  unitValue?: number;
  marketPrice?: number;
  estimatedValue?: number;
  nrvValue?: number;
}

export interface MillingRecipe {
  id?: string;
  recipeCode: string;
  recipeName: string;
  description?: string;
  version?: string;
  inputItemId: string;
  inputItemCode: string;
  inputItemName: string;
  inputUom: string;
  standardInputQty: number;
  outputItemId: string;
  outputItemCode: string;
  outputItemName: string;
  outputUom: string;
  standardOutputQty: number;
  expectedYieldPercent?: number;
  minYieldPercent?: number;
  maxYieldPercent?: number;
  standardLaborCost?: number;
  standardOverheadCost?: number;
  standardDepreciationCost?: number;
  standardUtilitiesCost?: number;
  standardTotalCost?: number;
  processingTimeMinutes?: number;
  machineId?: string;
  machineName?: string;
  status: RecipeStatus;
  isDefault?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
  companyId?: string;
  facilityId?: string;
  createdBy: string;
  createdAt?: string;
  updatedAt?: string;
  inputRows?: MillingRecipeInputRow[];
  outputRows?: MillingRecipeOutputRow[];
}

// Order Interfaces
export interface MillingOrderInputRow {
  id?: string;
  lineNo: number;
  millingOrderId?: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  plannedQty: number;
  actualQty?: number;
  varianceQty?: number;
  warehouseId?: string;
  warehouseName?: string;
  batchNo?: string;
  lotNo?: string;
  unitCost?: number;
  totalCost?: number;
  isIssued?: boolean;
  issuedAt?: string;
  issuedBy?: string;
  inventoryMovementId?: string;
}

export interface MillingOrderOutputRow {
  id?: string;
  lineNo: number;
  millingOrderId?: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  outputType: OutputType;
  isByProduct?: boolean;
  isMainProduct?: boolean;
  plannedQty: number;
  actualQty?: number;
  varianceQty?: number;
  yieldPercent?: number;
  warehouseId?: string;
  warehouseName?: string;
  batchNo?: string;
  lotNo?: string;
  allocatedCost?: number;
  unitCost?: number;
  marketValue?: number;
  nrvValue?: number;
  isReceived?: boolean;
  receivedAt?: string;
  receivedBy?: string;
  inventoryMovementId?: string;
  qualityGrade?: string;
  qualityNotes?: string;
}

export interface MillingOrderCostRow {
  id?: string;
  lineNo: number;
  millingOrderId?: string;
  costConfigId?: string;
  costType: CostType;
  costDescription?: string;
  plannedAmount?: number;
  actualAmount?: number;
  varianceAmount?: number;
  rate?: number;
  quantity?: number;
  accountId?: string;
  accountCode?: string;
  journalEntryId?: string;
}

export interface MillingOrder {
  id?: string;
  orderNumber: string;
  orderDate: string;
  weekNo?: number;
  recipeId: string;
  recipeCode: string;
  recipeName: string;
  status: OrderStatus;
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  plannedInputQty: number;
  plannedOutputQty: number;
  plannedYieldPercent?: number;
  actualInputQty?: number;
  actualOutputQty?: number;
  actualYieldPercent?: number;
  yieldVariance?: number;
  yieldVariancePercent?: number;
  plannedTotalCost?: number;
  actualTotalCost?: number;
  costVariance?: number;
  actualLaborCost?: number;
  actualMaterialCost?: number;
  actualOverheadCost?: number;
  actualDepreciationCost?: number;
  actualUtilitiesCost?: number;
  actualOtherCost?: number;
  unitCost?: number;
  machineId?: string;
  machineName?: string;
  operatorId?: string;
  operatorName?: string;
  inputWarehouseId?: string;
  inputWarehouseName?: string;
  outputWarehouseId?: string;
  outputWarehouseName?: string;
  companyId?: string;
  facilityId?: string;
  notes?: string;
  qualityNotes?: string;
  createdBy: string;
  createdAt?: string;
  updatedAt?: string;
  inputRows?: MillingOrderInputRow[];
  outputRows?: MillingOrderOutputRow[];
  costRows?: MillingOrderCostRow[];
}

// Cost Config Interface
export interface MillingCostConfig {
  id?: string;
  configCode: string;
  configName: string;
  description?: string;
  companyId?: string;
  facilityId?: string;
  recipeId?: string;
  costType: CostType;
  ratePerUnit?: number;
  ratePerHour?: number;
  fixedCost?: number;
  allocationMethod: AllocationMethod;
  allocationToByproductPercent?: number;
  costAccountId?: string;
  costAccountCode?: string;
  priority?: number;
  isDefault?: boolean;
  isActive?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
  createdBy: string;
  createdAt?: string;
  updatedAt?: string;
}

// API Response Types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// API Service Class
class MillingApiService {
  private baseURL = MILLING_API_BASE_URL;

  // ========== RECIPES ==========
  async getRecipes(params?: { page?: number; limit?: number; status?: RecipeStatus; search?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/recipes`, { params });
    return response.data;
  }

  async getRecipe(id: string): Promise<MillingRecipe> {
    const response = await axios.get(`${this.baseURL}/milling/recipes/${id}`);
    return response.data;
  }

  async createRecipe(data: Partial<MillingRecipe>): Promise<MillingRecipe> {
    const response = await axios.post(`${this.baseURL}/milling/recipes`, data);
    return response.data;
  }

  async updateRecipe(id: string, data: Partial<MillingRecipe>): Promise<MillingRecipe> {
    const response = await axios.patch(`${this.baseURL}/milling/recipes/${id}`, data);
    return response.data;
  }

  async deleteRecipe(id: string): Promise<void> {
    await axios.delete(`${this.baseURL}/milling/recipes/${id}`);
  }

  async duplicateRecipe(id: string, data: { newRecipeCode: string; createdBy: string }): Promise<MillingRecipe> {
    const response = await axios.post(`${this.baseURL}/milling/recipes/${id}/duplicate`, data);
    return response.data;
  }

  async activateRecipe(id: string, updatedBy: string): Promise<MillingRecipe> {
    const response = await axios.post(`${this.baseURL}/milling/recipes/${id}/activate`, { updatedBy });
    return response.data;
  }

  async deactivateRecipe(id: string, updatedBy: string): Promise<MillingRecipe> {
    const response = await axios.post(`${this.baseURL}/milling/recipes/${id}/deactivate`, { updatedBy });
    return response.data;
  }

  // ========== ORDERS ==========
  async getOrders(params?: { page?: number; limit?: number; status?: OrderStatus; recipeId?: string; fromDate?: string; toDate?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/orders`, { params });
    return response.data;
  }

  async getOrder(id: string): Promise<MillingOrder> {
    const response = await axios.get(`${this.baseURL}/milling/orders/${id}`);
    return response.data;
  }

  async createOrder(data: Partial<MillingOrder>): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders`, data);
    return response.data;
  }

  async createOrderFromRecipe(recipeId: string, data: Partial<MillingOrder>): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/from-recipe/${recipeId}`, data);
    return response.data;
  }

  async updateOrder(id: string, data: Partial<MillingOrder>): Promise<MillingOrder> {
    const response = await axios.patch(`${this.baseURL}/milling/orders/${id}`, data);
    return response.data;
  }

  async deleteOrder(id: string): Promise<void> {
    await axios.delete(`${this.baseURL}/milling/orders/${id}`);
  }

  // Workflow Actions
  async submitForApproval(id: string, data: { updatedBy: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/submit-for-approval`, data);
    return response.data;
  }

  async approveOrder(id: string, data: { approvedBy: string; notes?: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/approve`, data);
    return response.data;
  }

  async startOrder(id: string, data: { updatedBy: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/start`, data);
    return response.data;
  }

  async completeOrder(id: string, data: { completedBy: string; notes?: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/complete`, data);
    return response.data;
  }

  async cancelOrder(id: string, data: { cancelledBy: string; reason?: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/cancel`, data);
    return response.data;
  }

  async holdOrder(id: string, data: { updatedBy: string; reason?: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/hold`, data);
    return response.data;
  }

  async resumeOrder(id: string, data: { updatedBy: string }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/resume`, data);
    return response.data;
  }

  async issueMaterials(id: string, data: { userId: string; inputRows: Array<{ inputRowId: string; actualQty: number; batchNo?: string; lotNo?: string }> }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/issue-materials`, data);
    return response.data;
  }

  async receiveOutputs(id: string, data: { userId: string; outputRows: Array<{ outputRowId: string; actualQty: number; batchNo?: string; lotNo?: string; qualityGrade?: string }> }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/receive-outputs`, data);
    return response.data;
  }

  async calculateCosts(id: string): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/calculate-costs`);
    return response.data;
  }

  async allocateCosts(id: string, data: { allocationMethod: AllocationMethod }): Promise<MillingOrder> {
    const response = await axios.post(`${this.baseURL}/milling/orders/${id}/allocate-costs`, data);
    return response.data;
  }

  async getVarianceReport(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/orders/${id}/variance-report`);
    return response.data;
  }

  // ========== COST CONFIGS ==========
  async getCostConfigs(params?: { costType?: CostType; isActive?: boolean; recipeId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/costs`, { params });
    return response.data;
  }

  async getCostConfig(id: string): Promise<MillingCostConfig> {
    const response = await axios.get(`${this.baseURL}/milling/costs/${id}`);
    return response.data;
  }

  async createCostConfig(data: Partial<MillingCostConfig>): Promise<MillingCostConfig> {
    const response = await axios.post(`${this.baseURL}/milling/costs`, data);
    return response.data;
  }

  async updateCostConfig(id: string, data: Partial<MillingCostConfig>): Promise<MillingCostConfig> {
    const response = await axios.patch(`${this.baseURL}/milling/costs/${id}`, data);
    return response.data;
  }

  async deleteCostConfig(id: string): Promise<void> {
    await axios.delete(`${this.baseURL}/milling/costs/${id}`);
  }

  async getEffectiveCostConfig(costType: CostType, params?: { recipeId?: string; facilityId?: string; companyId?: string }): Promise<MillingCostConfig> {
    const response = await axios.get(`${this.baseURL}/milling/costs/effective/${costType}`, { params });
    return response.data;
  }

  // ========== GRAIN LOTS ==========
  async getGrainLots(params?: { status?: string; grainType?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/grain-lots`, { params });
    return response.data;
  }

  async getGrainLot(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/grain-lots/${id}`);
    return response.data;
  }

  async createGrainLot(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/grain-lots`, data);
    return response.data;
  }

  async updateGrainLot(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/grain-lots/${id}`, data);
    return response.data;
  }

  async approveGrainLot(id: string, approvedBy: string) {
    const response = await axios.patch(`${this.baseURL}/milling/grain-lots/${id}/approve`, { approvedBy });
    return response.data;
  }

  async rejectGrainLot(id: string, notes: string) {
    const response = await axios.patch(`${this.baseURL}/milling/grain-lots/${id}/reject`, { notes });
    return response.data;
  }

  async assignGrainLotToSilo(id: string, siloId: string, siloCode: string) {
    const response = await axios.patch(`${this.baseURL}/milling/grain-lots/${id}/assign-silo`, { siloId, siloCode });
    return response.data;
  }

  async deleteGrainLot(id: string) {
    await axios.delete(`${this.baseURL}/milling/grain-lots/${id}`);
  }

  // ========== SILOS ==========
  async getSilos() {
    const response = await axios.get(`${this.baseURL}/milling/silos`);
    return response.data;
  }

  async getSilo(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/silos/${id}`);
    return response.data;
  }

  async createSilo(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/silos`, data);
    return response.data;
  }

  async updateSilo(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/silos/${id}`, data);
    return response.data;
  }

  async deleteSilo(id: string) {
    await axios.delete(`${this.baseURL}/milling/silos/${id}`);
  }

  // ========== GRIST RECIPES ==========
  async getGristRecipes(params?: { status?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/grist-recipes`, { params });
    return response.data;
  }

  async getGristRecipe(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/grist-recipes/${id}`);
    return response.data;
  }

  async createGristRecipe(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/grist-recipes`, data);
    return response.data;
  }

  async updateGristRecipe(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/grist-recipes/${id}`, data);
    return response.data;
  }

  async activateGristRecipe(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/grist-recipes/${id}/activate`);
    return response.data;
  }

  async deactivateGristRecipe(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/grist-recipes/${id}/deactivate`);
    return response.data;
  }

  async deleteGristRecipe(id: string) {
    await axios.delete(`${this.baseURL}/milling/grist-recipes/${id}`);
  }

  // ========== TEMPERING ==========
  async getTemperingRecords(params?: { status?: string; grainLotId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/tempering`, { params });
    return response.data;
  }

  async getTemperingRecord(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/tempering/${id}`);
    return response.data;
  }

  async createTemperingRecord(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/tempering`, data);
    return response.data;
  }

  async updateTemperingRecord(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/tempering/${id}`, data);
    return response.data;
  }

  async startFirstTemper(id: string, waterAddedLiters?: number) {
    const response = await axios.patch(`${this.baseURL}/milling/tempering/${id}/start-first-temper`, { waterAddedLiters });
    return response.data;
  }

  async endFirstTemper(id: string, moisture?: number) {
    const response = await axios.patch(`${this.baseURL}/milling/tempering/${id}/end-first-temper`, { moisture });
    return response.data;
  }

  async endSecondTemper(id: string, moisture?: number, finalMoisture?: number) {
    const response = await axios.patch(`${this.baseURL}/milling/tempering/${id}/end-second-temper`, { moisture, finalMoisture });
    return response.data;
  }

  async markMilled(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/tempering/${id}/mark-milled`);
    return response.data;
  }

  // ========== QUALITY TESTS ==========
  async getQualityTests(params?: { status?: string; sampleType?: string; referenceId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/quality-tests`, { params });
    return response.data;
  }

  async getQualityTest(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/quality-tests/${id}`);
    return response.data;
  }

  async createQualityTest(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/quality-tests`, data);
    return response.data;
  }

  async updateQualityTest(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/quality-tests/${id}`, data);
    return response.data;
  }

  async approveQualityTest(id: string, approvedBy?: string) {
    const response = await axios.patch(`${this.baseURL}/milling/quality-tests/${id}/approve`, { approvedBy });
    return response.data;
  }

  async passQualityTest(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/quality-tests/${id}/pass`);
    return response.data;
  }

  async failQualityTest(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/quality-tests/${id}/fail`);
    return response.data;
  }

  async deleteQualityTest(id: string) {
    await axios.delete(`${this.baseURL}/milling/quality-tests/${id}`);
  }

  async getCoa(referenceId: string) {
    const response = await axios.get(`${this.baseURL}/milling/quality-tests/coa/${referenceId}`);
    return response.data;
  }

  // ========== AFLATOXIN ==========
  async getAflatoxinRecords(params?: { status?: string; sampleSource?: string; referenceId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/aflatoxin`, { params });
    return response.data;
  }

  async getAflatoxinRecord(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/aflatoxin/${id}`);
    return response.data;
  }

  async createAflatoxinRecord(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/aflatoxin`, data);
    return response.data;
  }

  async updateAflatoxinRecord(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/aflatoxin/${id}`, data);
    return response.data;
  }

  async certifyAflatoxin(id: string, certifiedBy: string) {
    const response = await axios.patch(`${this.baseURL}/milling/aflatoxin/${id}/certify`, { certifiedBy });
    return response.data;
  }

  async getAflatoxinTrend(days = 30) {
    const response = await axios.get(`${this.baseURL}/milling/aflatoxin/trend`, { params: { days } });
    return response.data;
  }

  async deleteAflatoxinRecord(id: string) {
    await axios.delete(`${this.baseURL}/milling/aflatoxin/${id}`);
  }

  // ========== FORTIFICATION ==========
  async getFortificationRecords(params?: { status?: string; millingOrderId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/fortification`, { params });
    return response.data;
  }

  async getFortificationRecord(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/fortification/${id}`);
    return response.data;
  }

  async createFortificationRecord(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/fortification`, data);
    return response.data;
  }

  async updateFortificationRecord(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/fortification/${id}`, data);
    return response.data;
  }

  async verifyFortification(id: string, verifiedBy: string, compliant: boolean) {
    const response = await axios.patch(`${this.baseURL}/milling/fortification/${id}/verify`, { verifiedBy, compliant });
    return response.data;
  }

  async deleteFortificationRecord(id: string) {
    await axios.delete(`${this.baseURL}/milling/fortification/${id}`);
  }

  // ========== DEGERMING ==========
  async getDegermingBatches(params?: { status?: string; grainLotId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/degerming`, { params });
    return response.data;
  }

  async getDegermingBatch(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/degerming/${id}`);
    return response.data;
  }

  async createDegermingBatch(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/degerming`, data);
    return response.data;
  }

  async updateDegermingBatch(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/degerming/${id}`, data);
    return response.data;
  }

  async completeDegerming(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/degerming/${id}/complete`);
    return response.data;
  }

  async deleteDegermingBatch(id: string) {
    await axios.delete(`${this.baseURL}/milling/degerming/${id}`);
  }

  // ========== MILLING SCHEDULE ==========
  async getMillingSchedule(params?: { status?: string; dateFrom?: string; dateTo?: string; millId?: string }) {
    const response = await axios.get(`${this.baseURL}/milling/schedule`, { params });
    return response.data;
  }

  async getMillingScheduleWeek(weekStart: string) {
    const response = await axios.get(`${this.baseURL}/milling/schedule/week`, { params: { weekStart } });
    return response.data;
  }

  async getMillingScheduleEntry(id: string) {
    const response = await axios.get(`${this.baseURL}/milling/schedule/${id}`);
    return response.data;
  }

  async createMillingScheduleEntry(data: any) {
    const response = await axios.post(`${this.baseURL}/milling/schedule`, data);
    return response.data;
  }

  async updateMillingScheduleEntry(id: string, data: any) {
    const response = await axios.put(`${this.baseURL}/milling/schedule/${id}`, data);
    return response.data;
  }

  async startMillingScheduleEntry(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/schedule/${id}/start`);
    return response.data;
  }

  async completeMillingScheduleEntry(id: string) {
    const response = await axios.patch(`${this.baseURL}/milling/schedule/${id}/complete`);
    return response.data;
  }

  async postponeMillingScheduleEntry(id: string, newDate: string) {
    const response = await axios.patch(`${this.baseURL}/milling/schedule/${id}/postpone`, { newDate });
    return response.data;
  }

  async deleteMillingScheduleEntry(id: string) {
    await axios.delete(`${this.baseURL}/milling/schedule/${id}`);
  }
}

export const millingApi = new MillingApiService();
export default millingApi;
